import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useWhiteLabelling } from 'components/WhiteLabellingProvider';
import useIsViewport from 'hooks/useIsViewport';
import useSearch from 'hooks/useSearch';
import { ProductData, ProductTypeSection } from 'generated/graphql';
import { DAYS_MOBILE, DAYS_DESKTOP } from './product-screens';

const ACTIVE_PARAM = 'a';
export enum ProductDialogs {
  OVERRIDE_PRICES = 'Override prices',
  ORDER_CUT_OFF = 'Order cut off',
  MARKET_PREVIEW = 'Market preview',
  DELETE_PRODUCT = 'Delete product',
}

export const Context = createContext<{
  selectedProduct: ProductData | Partial<ProductData> | undefined;
  selectedProductType: ProductTypeSection | undefined;
  dialog?: string | null;
  screenSizeDays: number;
  hasSavedChanges: boolean;
  setHasSavedChanges: (value: boolean) => void;
  onShowDialog: (dialog: string | null) => void;
  onSelectProduct: (productData: ProductData, productType: ProductTypeSection) => void;
} | null>(null);

export const ProductsUIProvider = ({ children }: { children: ReactNode }) => {
  const { isInputsType } = useWhiteLabelling();
  const isMobile = useIsViewport();
  const [getParam, setParam] = useSearch();
  const currentActiveValue = getParam(ACTIVE_PARAM) ?? '';

  const [selectedProduct, setSelectedProduct] = useState<ProductData | Partial<ProductData> | undefined>({
    sku: currentActiveValue,
  });
  const [selectedProductType, setSelectedProductType] = useState<ProductTypeSection>();
  const [dialog, setDialog] = useState<string | null>();
  const [hasSavedChanges, setHasSavedChanges] = useState(false);

  const screenSizeDays = isInputsType ? 1 : isMobile ? DAYS_MOBILE : DAYS_DESKTOP;

  const handleSelectProduct = useCallback(
    (productData: ProductData, productType: ProductTypeSection) => {
      setSelectedProduct(productData);
      setParam(ACTIVE_PARAM, productData.sku);
      setSelectedProductType(productType);
    },
    [setParam],
  );

  const handleShowDialog = useCallback((dialog: string | null) => {
    setDialog(dialog);
  }, []);

  const value = useMemo(
    () => ({
      selectedProduct,
      selectedProductType,
      dialog,
      screenSizeDays,
      hasSavedChanges,
      setHasSavedChanges,
      onShowDialog: handleShowDialog,
      onSelectProduct: handleSelectProduct,
    }),
    [
      selectedProduct,
      handleSelectProduct,
      selectedProductType,
      dialog,
      handleShowDialog,
      screenSizeDays,
      hasSavedChanges,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useProductsUI = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useProductsUI must be used within ProductsUIProvider');
  }
  return result;
};
